/**
 * Discover Page - Browse Public VR Experiences
 */

import { getCurrentUser, onAuthChange } from '../src/firebase/auth';
import {
  listDiscoverableSequences,
  type Sequence,
  isPasswordProtected
} from '../src/api/SequenceAPI';
import { listUserOrganizations, type Organization } from '../src/api/OrganizationAPI';
import { getUserProfile, ensureUserProfile, hasFeatureAccess, toggleUserAnalytics, type UserProfile } from '../src/api/UserProfileAPI';
import { showUpgradeModal } from '../src/ui/UpgradeModal';
import { initToolbar, updateToolbarProfile, triggerToolbarAnimations } from '../src/ui/UserDropdown';
import { initWebVitals } from '../src/utils/webVitals';
import { analytics } from '../src/firebase/config';

// Initialize Web Vitals tracking
initWebVitals(analytics);

// DOM Elements
const loadingState = document.getElementById('loading-state') as HTMLElement;
const sequencesGrid = document.getElementById('sequences-grid') as HTMLElement;
const emptyState = document.getElementById('empty-state') as HTMLElement;
const authButtons = document.getElementById('auth-buttons') as HTMLElement;
const userInfoLoggedIn = document.getElementById('user-info-logged-in') as HTMLElement;
const mobileAuthButtons = document.getElementById('mobile-auth-buttons') as HTMLElement;
const mobileUserActions = document.getElementById('mobile-user-actions') as HTMLElement;
const userEmailSpan = document.getElementById('user-email') as HTMLElement;
const mobileUserEmailSpan = document.getElementById('mobile-user-email') as HTMLElement;
// Note: settingsBtn, signOutBtn, usageBtn are now handled by shared initToolbar
const upgradeBtn = document.getElementById('upgradeBtn') as HTMLButtonElement;
const proBadge = document.getElementById('pro-badge') as HTMLElement | null;
const userSettingsModal = document.getElementById('user-settings-modal') as HTMLElement;
const userSettingsForm = document.getElementById('user-settings-form') as HTMLFormElement;
const cancelUserSettingsBtn = document.getElementById('cancel-user-settings-btn') as HTMLButtonElement;
const userAnalyticsCheckbox = document.getElementById('user-analytics-enabled') as HTMLInputElement | null;

const userDropdown = document.getElementById('user-dropdown') as HTMLElement;
const userDropdownToggle = document.getElementById('user-dropdown-toggle') as HTMLElement;

// State
let currentUser: any = null;
let currentUserProfile: UserProfile | null = null;
let sequences: Sequence[] = [];
let organizations: Organization[] = [];

/**
 * Initialize the discover page
 */
async function init() {
  console.log('🎨 Initializing Discover Page...');

  // Listen for auth changes
  onAuthChange(async (user) => {
    currentUser = user;

    if (user) {
      // User is signed in
      authButtons.style.display = 'none';
      userInfoLoggedIn.style.display = 'flex';

      // Update user email in both desktop and mobile
      if (userEmailSpan) {
        userEmailSpan.textContent = user.email || 'User';
      }
      if (mobileAuthButtons) mobileAuthButtons.style.display = 'none';
      if (mobileUserActions) mobileUserActions.style.display = 'flex';
      if (mobileUserEmailSpan) {
        mobileUserEmailSpan.style.display = 'block';
        mobileUserEmailSpan.textContent = user.email || 'User';
      }

      // Load profile (needed for tier badge)
      if (user.email) {
        currentUserProfile = await ensureUserProfile(user.uid, user.email, user.displayName);

        // Initialize shared toolbar (dropdown, usage, settings, signout, tier badge)
        initToolbar({
          userProfile: currentUserProfile,
          userId: user.uid,
          animationClass: 'header-toolbar-animate',
          triggerAnimations: false, // We'll trigger after setup
        });

        // Show/hide admin dashboard button based on tier
        const adminDashboardBtn = document.getElementById('admin-dashboard-btn');
        if (adminDashboardBtn && currentUserProfile) {
          adminDashboardBtn.style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
        }

        // Show/hide admin items in mobile menu
        if (currentUserProfile) {
          document.querySelectorAll('.admin-mobile-btn').forEach((el) => {
            (el as HTMLElement).style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
          });
          // Show/hide admin items in dropdown menu
          document.querySelectorAll('.admin-dropdown-btn').forEach((el) => {
            (el as HTMLElement).style.display = currentUserProfile.tier === 'admin' ? 'flex' : 'none';
          });
        }
      }

      // Trigger toolbar animations NOW - we have user profile/tier info
      triggerToolbarAnimations('header-toolbar-animate');

      // Load organizations in background (not needed for toolbar)
      listUserOrganizations(user.uid).then(orgs => {
        organizations = orgs;
      });
    } else {
      // User is not signed in
      authButtons.style.display = 'flex';
      userInfoLoggedIn.style.display = 'none';

      if (mobileAuthButtons) mobileAuthButtons.style.display = 'flex';
      if (mobileUserActions) mobileUserActions.style.display = 'none';
      if (mobileUserEmailSpan) mobileUserEmailSpan.style.display = 'none';

      currentUserProfile = null;

      // Hide upgrade button and pro badge for logged out users
      if (upgradeBtn) upgradeBtn.style.display = 'none';
      if (proBadge) proBadge.style.display = 'none';

      // Trigger toolbar animations for logged out users
      triggerToolbarAnimations('header-toolbar-animate');
    }

    // Load discoverable sequences (available to everyone)
    await loadSequences();
  });

  setupEventListeners();
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
  // Note: signOut, settings, usage button handlers are now set up by initToolbar

  if (cancelUserSettingsBtn) {
    cancelUserSettingsBtn.addEventListener('click', () => {
      userSettingsModal.classList.remove('show');
    });
  }

  if (userSettingsModal) {
    userSettingsModal.addEventListener('click', (e) => {
      if (e.target === userSettingsModal) {
        userSettingsModal.classList.remove('show');
      }
    });
  }

  if (userSettingsForm) {
    userSettingsForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!currentUser || !userAnalyticsCheckbox) return;

      try {
        const enabled = userAnalyticsCheckbox.checked;
        await toggleUserAnalytics(currentUser.uid, enabled);

        // Update local profile
        if (currentUserProfile) {
          currentUserProfile.analyticsEnabled = enabled;
        }

        userSettingsModal.classList.remove('show');
        alert('Settings saved successfully');
      } catch (error) {
        console.error('Failed to save settings:', error);
        alert('Failed to save settings');
      }
    });
  }

  if (upgradeBtn) {
    upgradeBtn.addEventListener('click', async () => {
      if (currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
      } else {
        window.location.href = '/dashboard/auth.html';
      }
    });
  }


  // Note: User dropdown toggle is now set up by initToolbar
}

/**
 * Load all discoverable sequences
 */
async function loadSequences() {
  try {
    loadingState.style.display = 'block';
    sequencesGrid.style.display = 'none';
    emptyState.style.display = 'none';

    sequences = await listDiscoverableSequences(100); // Load up to 100 sequences

    if (sequences.length === 0) {
      loadingState.style.display = 'none';
      emptyState.style.display = 'block';
    } else {
      loadingState.style.display = 'none';
      sequencesGrid.style.display = 'grid';
      renderSequences();
    }
  } catch (error) {
    console.error('❌ Error loading sequences:', error);
    loadingState.style.display = 'none';
    emptyState.style.display = 'block';
  }
}

/**
 * Render sequences in the grid
 */
async function renderSequences() {
  sequencesGrid.innerHTML = '';

  for (const sequence of sequences) {
    const card = document.createElement('div');
    card.className = 'sequence-card';
    card.onclick = () => {
      window.location.href = `/${sequence.id}`;
    };

    // Get creator display name if possible
    let creatorName = 'Anonymous Creator';
    try {
      const profile = await getUserProfile(sequence.userId);
      if (profile?.displayName) {
        creatorName = profile.displayName;
      } else if (profile?.email) {
        creatorName = profile.email.split('@')[0];
      }
    } catch (error) {
      // Silently fail - will use default creator name
    }

    // Format date
    const createdDate = sequence.createdAt?.toDate?.() || new Date();
    const formattedDate = createdDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Build badges
    let badges = '<span class="badge badge-discoverable">Public</span>';
    if (isPasswordProtected(sequence)) {
      badges += ' <span class="badge badge-password">Password Protected</span>';
    }

    // Build thumbnail section
    const thumbnailHtml = sequence.thumbnailUrl
      ? `<div class="sequence-thumbnail">
           <img src="${escapeHtml(sequence.thumbnailUrl)}" alt="${escapeHtml(sequence.name)}" loading="lazy">
         </div>`
      : `<div class="sequence-thumbnail sequence-thumbnail-placeholder">
           <div class="placeholder-content">
             <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
               <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
               <circle cx="12" cy="12" r="3"></circle>
             </svg>
             <span>No Preview</span>
           </div>
         </div>`;

    card.innerHTML = `
      ${thumbnailHtml}
      <div class="sequence-header">
        <div>
          <div class="sequence-name">${escapeHtml(sequence.name)}</div>
          <div class="sequence-creator">by ${escapeHtml(creatorName)}</div>
        </div>
      </div>
      ${sequence.description ? `<div class="sequence-description">${escapeHtml(sequence.description)}</div>` : ''}
      <div class="sequence-meta">
        ${badges}
        <span>•</span>
        <span>${formattedDate}</span>
      </div>
    `;

    sequencesGrid.appendChild(card);
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// triggerToolbarAnimations, setBadgeTier, updateProBadge moved to shared UserDropdown.ts

/**
 * Open user settings modal
 */
async function openUserSettings() {
  if (!currentUserProfile) return;

  try {
    // Set checkbox state
    if (userAnalyticsCheckbox) {
      userAnalyticsCheckbox.checked = currentUserProfile.analyticsEnabled !== false;
    }

    userSettingsModal.classList.add('show');
  } catch (error) {
    console.error('Error opening settings:', error);
  }
}

// Initialize on page load
init();
